import { getApiUrl } from "../utils/apiConfig";

const FULL_WINE_CLUB_INFO_PATH = "/fullWineClubInfo";
const UPDATE_WINE_CLUB_SUBSCRIPTION_ORDER_PATH = "/updateSubscriptionLineItems";

/**
 * `{ success, fullWineInfo }` or legacy root payload.
 * @param {object|null|undefined} raw
 * @returns {object|null}
 */
function normalizeFullWineClubInfoResponse(raw) {
  if (raw == null || typeof raw !== "object") return null;
  if (raw.success === false) {
    throw new Error(String(raw.message || raw.error || "fullWineClubInfo failed"));
  }
  if (raw.fullWineInfo != null && typeof raw.fullWineInfo === "object" && !Array.isArray(raw.fullWineInfo)) {
    return raw.fullWineInfo;
  }
  if (
    raw.customerId != null ||
    raw.subscriptionContractId != null ||
    raw.latestSubscriptionOrder != null ||
    raw.membershipDetails != null ||
    Array.isArray(raw.paymentMethod) ||
    raw.selectedPaymentMethod != null ||
    Array.isArray(raw.addresses) ||
    raw.activeSubscription != null
  ) {
    return raw;
  }
  return null;
}

export function getCustomerId() {
  if (typeof window === "undefined") return null;
  const id = window.customer_id;
  if (id == null || id === "" || id === "null") return null;
  const num = Number(id);
  return Number.isNaN(num) ? id : num;
}

export async function getFullWineClubInfo(customerId = null) {
  const cid = customerId ?? getCustomerId();
  if (!cid) return null;
  const shopDomain =
    typeof window !== "undefined" && window.location?.hostname?.includes(".myshopify.com")
      ? window.location.hostname
      : null;
  const url = getApiUrl(shopDomain, FULL_WINE_CLUB_INFO_PATH);
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_id: String(cid) }),
  });
  if (!res.ok) throw new Error(`fullWineClubInfo failed: ${res.status}`);
  return normalizeFullWineClubInfoResponse(await res.json());
}

function normalizeSubscriptionContractIdForApi(subscriptionContractId) {
  const idStr = String(subscriptionContractId || "").trim();
  if (!idStr) return "";
  const m = idStr.match(/SubscriptionContract\/(\d+)/i);
  if (m) return m[1];
  const digits = idStr.replace(/\D/g, "");
  return digits || idStr;
}

export async function updateWineClubSubscriptionOrderLines(subscriptionContractId, lines) {
  const contractId = normalizeSubscriptionContractIdForApi(subscriptionContractId);
  if (!contractId) throw new Error("subscription_contract_id is required");
  const cid = getCustomerId();
  if (cid == null || cid === "") throw new Error("customer_id is required");
  if (!Array.isArray(lines) || lines.length === 0) {
    throw new Error("No product lines to save");
  }
  const shopDomain =
    typeof window !== "undefined" && window.location?.hostname?.includes(".myshopify.com")
      ? window.location.hostname
      : null;
  const url = getApiUrl(shopDomain, UPDATE_WINE_CLUB_SUBSCRIPTION_ORDER_PATH);
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      customer_id: String(cid),
      subscription_contract_id: contractId,
      lines: lines.map((l) => {
        const pr = Number(l.price);
        return {
          variant_id: String(l.variantId),
          quantity: Math.max(1, Number(l.quantity) || 1),
          line_type: l.lineType,
          price: Number.isFinite(pr) ? pr : 0,
        };
      }),
    }),
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const msg =
      (data && (data.message || data.error || data.errors?.[0])) ||
      `updateSubscriptionLineItems failed: ${res.status}`;
    throw new Error(msg);
  }
  return data ?? {};
}
